import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { Card, Btn, Empty } from "../components/UI";
import { DateField } from "./engagement/DateField";
import { CocoaSelect } from "../components/CocoaSelect";
import { SlidePanel } from "../components/SlidePanel";
import { Activity, Download, TrendingUp, Building2, Clock, User as UserIcon, X } from "lucide-react";
import dayjs from "dayjs";
import { toast } from "sonner";

const BAND_CELL = {
  green: "bg-[#E8F5E9] text-[#2E7D32] border-[#C8E6C9]",
  amber: "bg-[#FFF8E1] text-[#876600] border-[#FFECB3]",
  red: "bg-[#FFEBEE] text-[#C62828] border-[#FFCDD2]",
};

function bandLabel(pct) {
  if (pct >= 71) return "Overbooked";
  if (pct >= 40) return "Busy";
  return "Light";
}

function toCsv(data) {
  const headers = ["Facility", "Room", "Unit", "Type"];
  data.weeks_meta.forEach((w) => {
    headers.push(`${w.label} hours`);
    headers.push(`${w.label} util%`);
  });
  const facMap = Object.fromEntries((data.facilities || []).map((f) => [f.id, f.name]));
  const rows = [headers.join(",")];
  data.rooms.forEach((r) => {
    const cells = [facMap[r.facility_id] || "", r.name, r.unit || "", r.room_type || ""];
    r.weeks.forEach((w) => {
      cells.push(w.hours);
      cells.push(w.util_pct);
    });
    rows.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
  });
  return rows.join("\n");
}

export default function RoomUtilization() {
  const monday = dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD");
  const [start, setStart] = useState(monday);
  const [weeks, setWeeks] = useState("4");
  const [facilityId, setFacilityId] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [drill, setDrill] = useState(null); // {room_id, room_name, week_meta}
  const [drillData, setDrillData] = useState(null);

  useEffect(() => {
    api.get("/facilities").then((r) => setFacilities(r.data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    api.get("/rooms/utilization", { params: { start, weeks: Number(weeks) || 4, facility_id: facilityId || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [start, weeks, facilityId]); // eslint-disable-line

  const roomsByFacility = useMemo(() => {
    if (!data) return {};
    const out = {};
    data.rooms.forEach((r) => {
      if (!out[r.facility_id]) out[r.facility_id] = [];
      out[r.facility_id].push(r);
    });
    return out;
  }, [data]);

  const downloadCsv = () => {
    if (!data) return;
    const csv = toCsv(data);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `room-utilization-${start}-${weeks}w.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDrill = (room, weekMeta) => {
    setDrill({ room, weekMeta });
    setDrillData(null);
    api.get(`/rooms/${room.id}/utilization/appointments`, { params: { start: weekMeta.start, end: weekMeta.end } })
      .then((r) => setDrillData(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  return (
    <div className="space-y-5" data-testid="room-utilization-section">
      <Card className="p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-[#7B6157]" />
              <h3 className="font-heading text-lg font-bold text-[#3E2723]">Room Utilization</h3>
            </div>
            <p className="text-xs text-[#7B6157]">
              Weekly heatmap of booked clinical hours against the 40-hour Mon-Fri capacity per room. Green is light, amber is busy, red is overbooked.
            </p>
          </div>
          <Btn variant="outline" data-testid="room-util-export" onClick={downloadCsv} disabled={!data}>
            <Download size={13} /> Export CSV
          </Btn>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="mb-0.5 text-[10px] font-bold uppercase text-[#7B6157]">Start (Mon)</div>
            <DateField testId="room-util-start" className="w-36" value={start} onChange={setStart} placeholder="Any Monday" />
          </div>
          <div>
            <div className="mb-0.5 text-[10px] font-bold uppercase text-[#7B6157]">Weeks</div>
            <CocoaSelect testId="room-util-weeks" className="w-24" value={weeks} onChange={setWeeks}
              options={[["2", "2"], ["4", "4"], ["6", "6"], ["8", "8"], ["12", "12"]]} />
          </div>
          <div>
            <div className="mb-0.5 text-[10px] font-bold uppercase text-[#7B6157]">Facility</div>
            <CocoaSelect testId="room-util-facility" className="w-64" value={facilityId} onChange={setFacilityId} placeholder="All facilities"
              options={facilities.filter((f) => f.status === "active").map((f) => [f.id, f.name])} />
          </div>
        </div>
      </Card>

      {!data ? (
        <div className="h-64 animate-pulse rounded-2xl bg-[#EFEBE9]" />
      ) : data.rooms.length === 0 ? (
        <Empty text="No active rooms match your filters" />
      ) : (
        <>
          {/* Facility rollup */}
          {data.facilities.map((f) => (
            <Card key={f.id} className="p-5" data-testid={`room-util-facility-${f.id}`}>
              <div className="mb-3 flex items-center gap-2">
                <Building2 size={15} className="text-[#7B6157]" />
                <h3 className="font-heading text-base font-bold text-[#3E2723]">{f.name}</h3>
                <span className="text-xs text-[#7B6157]">{f.rooms} active room{f.rooms === 1 ? "" : "s"}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="w-56 py-2 pr-3 text-left text-[11px] font-bold uppercase text-[#7B6157]">Room</th>
                      {data.weeks_meta.map((w) => (
                        <th key={w.index} className="px-2 py-2 text-center text-[11px] font-bold uppercase text-[#7B6157]">
                          {w.label}
                          <div className="text-[9px] font-normal text-[#84675B]">Week {w.index + 1}</div>
                        </th>
                      ))}
                    </tr>
                    <tr data-testid={`room-util-facility-rollup-${f.id}`}>
                      <td className="py-2 pr-3 text-xs font-bold text-[#4E342E]"><TrendingUp size={13} className="inline mr-1" /> Facility total</td>
                      {f.weeks.map((w, i) => (
                        <td key={i} className={`px-2 py-2 border ${BAND_CELL[w.band]} text-center`}
                          data-testid={`room-util-facility-week-${f.id}-${i}`}>
                          <div className="text-xs font-black tabular-nums">{w.util_pct}%</div>
                          <div className="text-[10px] font-semibold">{w.hours}h · {w.count}</div>
                        </td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(roomsByFacility[f.id] || []).map((rm) => (
                      <tr key={rm.id} data-testid={`room-util-row-${rm.id}`} className="border-t border-[#EFEBE0]">
                        <td className="py-2 pr-3">
                          <div className="font-semibold text-[#3E2723] text-sm">{rm.name}</div>
                          <div className="text-[10px] text-[#7B6157]">{[rm.room_type, rm.unit, rm.floor && `Floor ${rm.floor}`].filter(Boolean).join(" · ")}</div>
                        </td>
                        {rm.weeks.map((w, i) => (
                          <td key={i} className="p-0 border" data-testid={`room-util-cell-${rm.id}-${i}`}>
                            <button type="button" onClick={() => openDrill(rm, data.weeks_meta[i])}
                              disabled={w.count === 0}
                              className={`w-full h-full px-2 py-2 text-center transition-colors ${BAND_CELL[w.band]} ${
                                w.count > 0 ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
                              title={w.count > 0 ? `Click to view ${w.count} appointment${w.count === 1 ? "" : "s"}` : "No appointments"}>
                              <div className="text-xs font-black tabular-nums">{w.util_pct}%</div>
                              <div className="text-[10px] font-semibold">{w.hours}h · {w.count}</div>
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3 text-xs text-[#7B6157]">
            <span className="font-bold">Legend:</span>
            <span className={`rounded-full border px-2.5 py-1 font-bold ${BAND_CELL.green}`}>Light · under 40%</span>
            <span className={`rounded-full border px-2.5 py-1 font-bold ${BAND_CELL.amber}`}>Busy · 40% to 70%</span>
            <span className={`rounded-full border px-2.5 py-1 font-bold ${BAND_CELL.red}`}>Overbooked · over 70%</span>
            <span className="ml-auto">Baseline capacity: 40 clinical hours per room per week ({data.appointments_counted} booked appointments in this window)</span>
          </div>
        </>
      )}

      <SlidePanel open={!!drill} onClose={() => setDrill(null)} testId="room-util-drill-panel"
        title={drill ? `${drill.room.name} · ${drill.weekMeta.label}` : "Appointments"}>
        {drill && (
          <div className="p-4 space-y-3" data-testid="room-util-drill-content">
            <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 text-xs text-[#7B6157]">
              Week of {dayjs(drill.weekMeta.start).format("MMM D, YYYY")} through {dayjs(drill.weekMeta.end).format("MMM D, YYYY")}.
            </div>
            {!drillData ? <div className="h-24 animate-pulse rounded-xl bg-[#EFEBE9]" /> :
              drillData.count === 0 ? <Empty text="No booked appointments in this window" /> :
              <>
                <div className="flex gap-2 flex-wrap">
                  <span className="rounded-full bg-[#4E342E] px-3 py-1 text-xs font-bold text-white" data-testid="room-util-drill-total">
                    {drillData.count} appointment{drillData.count === 1 ? "" : "s"} · {drillData.total_hours}h
                  </span>
                </div>
                <div className="space-y-1.5">
                  {drillData.appointments.map((a) => (
                    <div key={a.id} data-testid={`room-util-drill-appt-${a.id}`}
                      className="flex flex-wrap items-center gap-3 rounded-xl border border-[#EFEBE0] bg-white p-3 text-sm">
                      <Clock size={13} className="text-[#7B6157]" />
                      <span className="font-semibold text-[#3E2723]">{dayjs(a.date).format("ddd MMM D")}</span>
                      <span className="text-[#7B6157]">{a.start_time} - {a.end_time}</span>
                      <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[11px] font-semibold text-[#6D4C41]">{a.type || "Session"}</span>
                      <UserIcon size={13} className="ml-auto text-[#7B6157]" />
                      <span className="text-xs text-[#3E2723]">{a.client_name}</span>
                      {a.bed && <span className="rounded bg-[#EDE7F6] px-1.5 py-0.5 text-[10px] font-semibold text-[#5E35B1]">Bed {a.bed}</span>}
                    </div>
                  ))}
                </div>
              </>
            }
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
